import { type ReactNode, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import { DetailsPanelWrapper } from "./details-panel-wrapper";

interface Props {
  enableSorting?: boolean;
  enableFiltering?: boolean;
  detailsPanelRenderer?: () => ReactNode;
  onSelect(val: "asc" | "desc" | "filter"): void;
}

export const HeaderMenu = (props: Props) => {
  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            className={clsx(
              "flex justify-center items-center rounded w-6 h-6 text-2xs",
              {
                "text-interstellar+2 hover:bg-cosmic-5": !open,
                "text-white bg-primary": open,
              },
            )}
          >
            <motion.i
              initial={false}
              animate={{ rotate: open ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-chevron-up"
            />
          </PopoverButton>
          <PopoverPanel
            anchor={{ to: "bottom start", gap: 2 }}
            className={clsx(
              "py-1 w-48 z-[1000] bg-white rounded-lg elevation-1 text-sm",
            )}
          >
            {({ close }) => <DropdownContent {...props} close={close} />}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const DropdownContent = ({
  enableSorting,
  enableFiltering,
  detailsPanelRenderer,
  onSelect,
  close,
}: Props & { close: () => void }) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [isAboutPanelVisible, setAboutPanelVisible] = useState(false);

  return (
    <>
      {detailsPanelRenderer && isAboutPanelVisible ? (
        <DetailsPanelWrapper
          onClose={() => {
            prettyLogEventPage({
              domain: "Table",
              action: "onClick",
              additionalActionInfo: "Hide About This Metric Info",
            });

            setAboutPanelVisible(false);
          }}
        >
          {detailsPanelRenderer()}
        </DetailsPanelWrapper>
      ) : (
        <>
          {enableSorting && (
            <>
              <div
                className={clsx(
                  "w-full text-left py-1.5 px-4 hover:bg-cosmic-4",
                  "truncate cursor-pointer",
                )}
                onClick={() => {
                  onSelect("desc");
                  close();
                }}
              >
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-arrow-down text-[0.8rem]",
                    "text-primary mt-0.5 mr-1 w-4",
                  )}
                />
                Sort descending
              </div>
              <div
                className={clsx(
                  "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate",
                  "cursor-pointer",
                )}
                onClick={() => {
                  onSelect("asc");
                  close();
                }}
              >
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-arrow-up text-[0.8rem]",
                    "text-primary mt-0.5 mr-1 w-4",
                  )}
                />
                Sort ascending
              </div>
            </>
          )}
          {enableFiltering && (
            <div
              className={clsx(
                "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
              )}
              onClick={() => {
                onSelect("filter");
                close();
              }}
            >
              <i
                className={clsx(
                  "fa-regular fa-sharp fa-filter text-[0.8rem]",
                  "text-primary mt-0.5 mr-1 w-4",
                )}
              />
              Filter
            </div>
          )}
          {detailsPanelRenderer && (
            <div
              className={clsx(
                "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
              )}
              onClick={() => {
                prettyLogEventPage({
                  domain: "Table",
                  action: "onClick",
                  additionalActionInfo: "Show About This Metric Info",
                });

                setAboutPanelVisible(true);
              }}
            >
              <i
                className={clsx(
                  "fa-regular fa-sharp fa-circle-info text-[0.8rem]",
                  "text-primary mt-0.5 mr-1 w-4",
                )}
              />
              About this metric
            </div>
          )}
        </>
      )}
    </>
  );
};
